import { useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Clock3, RotateCcw, CalendarCheck2, CalendarX2, Hourglass, Download, CalendarPlus, Check, MessageCircle, Send, Link2, ImageDown } from "lucide-react";
import { useI18n, fmtDate } from "../i18n";
import { useSoundOn, useResultSound } from "../sound";
import SoundControls from "./SoundControls";
import { shareWhatsApp, shareTelegram, copyText, summarySisa, IMG_LABEL } from "../share";
import { downloadResultImage } from "../resultImage";
import { AnimatedNumber } from "./AnimatedNumber";
import Tilt from "./Tilt";

const DURATIONS = [
    { key: "1m", label: "1 Minggu", days: 7, tid: "durasi-1-minggu" },
    { key: "1b", label: "1 Bulan", days: 30, tid: "durasi-1-bulan" },
    { key: "3b", label: "3 Bulan", days: 90, tid: "durasi-3-bulan" },
    { key: "6b", label: "6 Bulan", days: 180, tid: "durasi-6-bulan" },
    { key: "1t", label: "1 Tahun", days: 365, tid: "durasi-1-tahun" },
    { key: "custom", label: "Custom", days: null, tid: "durasi-custom" },
];

const toMid = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
const diffDays = (a, b) => Math.round((toMid(a) - toMid(b)) / 86400000);
const ymd = (d) =>
    `${d.getFullYear()}${String(d.getMonth() + 1).padStart(2, "0")}${String(d.getDate()).padStart(2, "0")}`;
const isoToday = () => {
    const t = new Date();
    return `${t.getFullYear()}-${String(t.getMonth() + 1).padStart(2, "0")}-${String(t.getDate()).padStart(2, "0")}`;
};

const shareBtn =
    "flex items-center gap-2 rounded-full border border-white/15 px-4 py-2 text-xs font-semibold text-zinc-300 transition-colors duration-200 hover:border-white/40 hover:text-white";

export default function SisaHari() {
    const { t, locale, lang } = useI18n();
    const [tanggal, setTanggal] = useState(isoToday());
    const [durKey, setDurKey] = useState("1b");
    const [custom, setCustom] = useState("");
    const [saved, setSaved] = useState(false);
    const [copied, setCopied] = useState(false);
    const [soundOn] = useSoundOn();

    const dur = DURATIONS.find((d) => d.key === durKey);
    const totalHari = dur.days ?? (+custom.replace(/\D/g, "") || 0);

    const data = useMemo(() => {
        if (!tanggal || totalHari <= 0) return null;
        const [y, m, dd] = tanggal.split("-").map(Number);
        const start = new Date(y, m - 1, dd);
        const end = new Date(y, m - 1, dd + totalHari);
        const berlalu = diffDays(new Date(), start);
        const sisa = totalHari - berlalu;
        return {
            start,
            end,
            berlalu: Math.max(0, berlalu),
            sisa,
            aktif: sisa > 0,
            pct: Math.min(100, Math.max(0, (berlalu / totalHari) * 100)),
        };
    }, [tanggal, totalHari]);

    useResultSound(data ? data.sisa : null, soundOn);

    const fmtShort = (d) => fmtDate(locale, d, { day: "numeric", month: "short", year: "numeric" });
    const fmtFull = (d) => fmtDate(locale, d, { weekday: "long", day: "numeric", month: "long", year: "numeric" });
    const nextDay = () => new Date(data.end.getTime() + 86400000);

    const downloadIcs = () => {
        const stamp = new Date().toISOString().replace(/[-:]/g, "").split(".")[0] + "Z";
        const ics = [
            "BEGIN:VCALENDAR",
            "VERSION:2.0",
            "PRODID:-//Refundaja//Pengingat Langganan//ID",
            "BEGIN:VEVENT",
            `UID:${Date.now()}@refundaja`,
            `DTSTAMP:${stamp}`,
            `DTSTART;VALUE=DATE:${ymd(data.end)}`,
            `DTEND;VALUE=DATE:${ymd(nextDay())}`,
            "SUMMARY:Langganan premium berakhir",
            `DESCRIPTION:Langganan ${totalHari} hari sejak ${fmtFull(data.start)}. Dibuat dengan Refundaja.`,
            "END:VEVENT",
            "END:VCALENDAR",
        ].join("\r\n");
        const url = URL.createObjectURL(new Blob([ics], { type: "text/calendar" }));
        const a = document.createElement("a");
        a.href = url;
        a.download = "pengingat-langganan.ics";
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
        setSaved(true);
        setTimeout(() => setSaved(false), 2000);
    };

    const gcalUrl = () => {
        const params = new URLSearchParams({
            action: "TEMPLATE",
            text: "Langganan premium berakhir",
            dates: `${ymd(data.end)}/${ymd(nextDay())}`,
            details: `Langganan ${totalHari} hari sejak ${fmtFull(data.start)}. Dibuat dengan Refundaja.`,
        });
        return `https://calendar.google.com/calendar/render?${params.toString()}`;
    };

    const shareText = data
        ? summarySisa(t, {
              startStr: fmtFull(data.start),
              endStr: fmtFull(data.end),
              totalHari,
              sisaHari: Math.max(0, data.sisa),
              aktif: data.aktif,
          })
        : "";
    const unduhGambar = () =>
        data &&
        downloadResultImage({
            filename: "refundaja-sisa-hari.png",
            title: t.t2,
            big: String(Math.max(0, data.sisa)),
            bigSuffix: t.s8,
            lines: [
                `${t.s11}: ${fmtShort(data.start)}`,
                `${t.s12}: ${fmtShort(data.end)}`,
                `${data.aktif ? t.s6 : t.s7} · ${totalHari} ${t.s9 === "days elapsed" ? "days" : "hari"}`,
            ],
        });

    return (
        <div className="grid gap-6 lg:grid-cols-2">
            <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.15 }}
                transition={{ duration: 0.7 }}
                className="glass flex flex-col gap-6 rounded-3xl p-6 sm:p-8"
                data-testid="panel-input-sisa"
            >
                <div className="flex items-center gap-3">
                    <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-brand-coral/20 via-brand-violet/20 to-brand-blue/20 text-brand-violet">
                        <Clock3 size={18} />
                    </span>
                    <h3 className="font-display text-xl font-bold">Input</h3>
                </div>

                <div className="flex flex-col gap-1.5">
                    <label className="font-mono text-[11px] font-bold uppercase tracking-[0.18em] text-zinc-500">
                        {t.s2}
                    </label>
                    <input
                        data-testid="input-tanggal"
                        type="date"
                        value={tanggal}
                        onChange={(e) => setTanggal(e.target.value)}
                        className="w-full rounded-xl border border-white/10 bg-white/[0.04] px-4 py-3 font-mono text-sm text-white outline-none transition-colors duration-200 focus:border-brand-violet/60 [color-scheme:dark]"
                    />
                </div>

                <div className="flex flex-col gap-3">
                    <label className="font-mono text-[11px] font-bold uppercase tracking-[0.18em] text-zinc-500">
                        {t.s3}
                    </label>
                    <div className="flex flex-wrap gap-2.5">
                        {DURATIONS.map((d) => (
                            <button
                                key={d.key}
                                onClick={() => setDurKey(d.key)}
                                data-testid={d.tid}
                                className={`relative rounded-full border px-5 py-2.5 text-sm font-semibold transition-colors duration-200 ${
                                    durKey === d.key
                                        ? "border-transparent text-[#0a0713]"
                                        : "border-white/15 text-zinc-300 hover:border-white/40"
                                }`}
                            >
                                {durKey === d.key && (
                                    <motion.span
                                        layoutId="durasi-pill"
                                        className="absolute inset-0 rounded-full bg-gradient-to-r from-brand-coral via-brand-violet to-brand-blue"
                                        transition={{ type: "spring", stiffness: 320, damping: 28 }}
                                    />
                                )}
                                <span className="relative z-10">{d.label}</span>
                            </button>
                        ))}
                    </div>
                    {durKey === "custom" && (
                        <div className="relative flex items-center">
                            <input
                                data-testid="input-custom-hari"
                                inputMode="numeric"
                                value={custom}
                                onChange={(e) => setCustom(e.target.value.replace(/\D/g, ""))}
                                placeholder={t.s4p}
                                className="w-full rounded-xl border border-white/10 bg-white/[0.04] px-4 py-3 font-mono text-sm text-white placeholder-zinc-600 outline-none transition-colors duration-200 focus:border-brand-violet/60"
                            />
                            <span className="pointer-events-none absolute right-4 font-mono text-xs text-zinc-500">
                                {t.s9 === "days elapsed" ? "days" : "hari"}
                            </span>
                        </div>
                    )}
                </div>

                <button
                    onClick={() => {
                        setTanggal(isoToday());
                        setDurKey("1b");
                        setCustom("");
                    }}
                    data-testid="button-reset-sisa"
                    className="mt-auto flex w-fit items-center gap-2 rounded-full border border-white/15 px-5 py-2.5 text-sm font-semibold text-zinc-300 transition-colors duration-200 hover:border-white/40"
                >
                    <RotateCcw size={14} /> {t.s5}
                </button>
            </motion.div>

            <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.15 }}
                transition={{ duration: 0.7, delay: 0.1 }}
                data-testid="panel-hasil-sisa"
            >
                <Tilt className="h-full">
                    <div className="relative flex h-full min-h-[380px] flex-col justify-between overflow-hidden rounded-3xl border border-white/10 bg-[#0a0713]/80 p-8 backdrop-blur-xl sm:p-10">
                        <div className="pointer-events-none absolute -right-20 -top-20 h-64 w-64 rounded-full bg-brand-violet/20 blur-[90px]" />
                        {data ? (
                            <>
                                <div>
                                    <div className="flex items-start justify-between">
                                        <span
                                            data-testid={data.aktif ? "status-aktif" : "status-kedaluwarsa"}
                                            className={`inline-flex items-center gap-2 rounded-full border px-4 py-1.5 text-xs font-bold ${
                                                data.aktif
                                                    ? "border-emerald-400/30 bg-emerald-400/10 text-emerald-300"
                                                    : "border-red-400/30 bg-red-400/10 text-red-300"
                                            }`}
                                        >
                                            <span className={`h-2 w-2 rounded-full ${data.aktif ? "bg-emerald-400" : "bg-red-400"}`} />
                                            {data.aktif ? t.s6 : t.s7}
                                        </span>
                                        <div className="flex items-center gap-2">
                                            <SoundControls testId="sound-toggle-sisa" />
                                            <Hourglass size={20} className={data.aktif ? "text-zinc-500" : "text-red-400"} />
                                        </div>
                                    </div>
                                    <div className="mt-6 flex items-baseline gap-3">
                                        <AnimatedNumber
                                            value={Math.max(0, data.sisa)}
                                            testId="hasil-sisa-hari"
                                            className="font-display text-6xl font-extrabold leading-none tracking-tight sm:text-7xl"
                                        />
                                        <span className="text-lg font-semibold text-zinc-500">{t.s8}</span>
                                    </div>
                                </div>

                                <div className="mt-8 space-y-5">
                                    <div data-testid="progress-sisa-masa">
                                        <div className="mb-2 flex justify-between text-xs text-zinc-400">
                                            <span>{data.berlalu} {t.s9}</span>
                                            <span className="font-mono text-zinc-500">{totalHari} {t.s10}</span>
                                        </div>
                                        <div className="h-1.5 w-full overflow-hidden rounded-full bg-white/[0.06]">
                                            <motion.div
                                                className="h-full rounded-full bg-gradient-to-r from-brand-coral via-brand-violet to-brand-blue"
                                                animate={{ width: `${data.pct}%` }}
                                                transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
                                            />
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-2 gap-4">
                                        <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-4" data-testid="hasil-mulai">
                                            <p className="mb-1 flex items-center gap-1.5 font-mono text-[10px] font-bold uppercase tracking-[0.16em] text-zinc-500">
                                                <CalendarCheck2 size={11} /> {t.s11}
                                            </p>
                                            <p className="font-mono text-sm font-bold text-white">{fmtShort(data.start)}</p>
                                        </div>
                                        <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-4" data-testid="hasil-berakhir">
                                            <p className="mb-1 flex items-center gap-1.5 font-mono text-[10px] font-bold uppercase tracking-[0.16em] text-zinc-500">
                                                <CalendarX2 size={11} /> {t.s12}
                                            </p>
                                            <p className="font-mono text-sm font-bold text-white">{fmtShort(data.end)}</p>
                                        </div>
                                    </div>

                                    <p className="text-center font-mono text-xs text-zinc-500" data-testid="hasil-berakhir-full">
                                        {fmtFull(data.end)}
                                    </p>

                                    <div className="flex flex-wrap justify-center gap-3 pt-1" data-testid="aksi-pengingat">
                                        <button
                                            onClick={downloadIcs}
                                            data-testid="button-ics"
                                            className="flex items-center gap-2 rounded-full bg-gradient-to-r from-brand-coral via-brand-violet to-brand-blue px-5 py-2.5 text-xs font-bold text-white transition-transform duration-200 hover:scale-[1.04] active:scale-95"
                                        >
                                            {saved ? <Check size={14} /> : <Download size={14} />}
                                            {saved ? t.s14 : t.s13}
                                        </button>
                                        <a
                                            href={gcalUrl()}
                                            target="_blank"
                                            rel="noreferrer"
                                            data-testid="button-gcal"
                                            className="flex items-center gap-2 rounded-full border border-white/15 px-5 py-2.5 text-xs font-semibold text-zinc-300 transition-colors duration-200 hover:border-white/40"
                                        >
                                            <CalendarPlus size={14} /> Google Calendar
                                        </a>
                                    </div>

                                    <div data-testid="share-row-sisa">
                                        <p className="mb-2 font-mono text-[10px] font-bold uppercase tracking-[0.18em] text-zinc-500">
                                            {t.s17}
                                        </p>
                                        <div className="flex flex-wrap gap-2.5">
                                            <button onClick={() => shareWhatsApp(shareText)} data-testid="share-wa-sisa" className={shareBtn}>
                                                <MessageCircle size={13} className="text-emerald-400" /> WhatsApp
                                            </button>
                                            <button onClick={() => shareTelegram(shareText)} data-testid="share-tg-sisa" className={shareBtn}>
                                                <Send size={13} className="text-sky-400" /> Telegram
                                            </button>
                                            <button
                                                onClick={async () => {
                                                    await copyText(shareText);
                                                    setCopied(true);
                                                    setTimeout(() => setCopied(false), 2000);
                                                }}
                                                data-testid="share-copy-sisa"
                                                className={shareBtn}
                                            >
                                                {copied ? <Check size={13} className="text-emerald-400" /> : <Link2 size={13} />}
                                                {copied ? t.s19 : t.s18}
                                            </button>
                                            <button onClick={unduhGambar} data-testid="share-image-sisa" className={shareBtn}>
                                                <ImageDown size={13} className="text-brand-violet" /> {IMG_LABEL[lang] || IMG_LABEL.id}
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </>
                        ) : (
                            <div data-testid="hasil-sisa-kosong">
                                <p className="font-display text-5xl font-extrabold text-zinc-800">—</p>
                                <p className="mt-3 text-xs text-zinc-600">{t.s16}</p>
                            </div>
                        )}
                    </div>
                </Tilt>
            </motion.div>
        </div>
    );
}
